import { ArrowDropDown } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    Stack,
    SxProps,
    TextField,
    Theme,
    ToggleButton,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import merge from 'lodash.merge';
import { useState, useRef, useEffect, useMemo } from 'react';
import { FixedSizeList } from 'react-window';

interface ToggleSelectProps {
    value: any;
    title: string;
    maxItemsOnView?: number;
    menuItemSize?: number;
    header?: string;
    tooltip?: string;
    options: { value: string; label: string }[];
    selections?: string[];
    selectAll?: boolean;
    deselectAll?: boolean;
    showCount?: boolean;
    showSelectAll?: boolean;
    showSelections?: boolean;
    showSearchbox?: boolean;
    abbreviations?: Map<string, string>;
    searchDelay?: number;
    disabled?: boolean;
    menuHeightAdjustment?: number;
    // menuWidth will override autoMenuWidthProps if set
    menuWidth?: number;
    autoMenuWidthProps?: {
        fontSize?: string;
        fontFamily?: string;
        checkboxWidth?: number;
        adjustment?: number;
    };
    sx?: {
        container?: SxProps<Theme>;
        button?: SxProps<Theme>;
        menu?: SxProps<Theme>;
        text?: SxProps<Theme>;
        checkbox?: SxProps<Theme>;
    };
    onChange?: (value: any, selectedOptions: string[]) => void;
}

const useToggleStyles = (theme: Theme): { [key: string]: any } => ({
    pill: {
        padding: '6px 12px',
        borderRadius: '6px',
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 'bold',
        width: 'fit-content',
        height: 'fit-content',
        paddingRight: '5px',
        backgroundColor: theme.palette.background.disabled,
        ':hover': {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.background.default
        },
        '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: theme.palette.action.selected,
            color: theme.palette.background.default
        },
        '&.Mui-disabled': {
            color: '#8D94A0'
        }
    },
    popUpContainer: { position: 'relative', top: '5px', zIndex: '99' },
    popUpMenu: {
        border: 1,
        minWidth: '120px',
        overflow: 'hidden',
        position: 'absolute',
        backgroundColor: theme.palette.background.default,
        borderColor: theme.palette.border.input,
        borderRadius: '6px',
        textWrap: 'nowrap',
        padding: '6px',
        gap: '0'
    },
    text: { fontSize: '14px' },
    checkboxLabel: { margin: '0', gap: '6px' },
    checkbox: { padding: '0' },
    divider: { width: '100%', marginBottom: '4px' }
});

const measureTextWidth = (text: string, font: string) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (context) {
        context.font = font;
        return context.measureText(text).width;
    }
    return 0;
};

// Note: this component does not work with ToggleButtonGroup
export const ToggleSelect = (props: ToggleSelectProps) => {
    const theme = useTheme();
    const styles = useToggleStyles(theme);

    const {
        value,
        title,
        maxItemsOnView = 10,
        menuItemSize = 24,
        header,
        tooltip,
        options,
        selections,
        selectAll,
        deselectAll,
        showCount = false,
        showSelectAll = true,
        showSelections = true,
        showSearchbox = false,
        searchDelay = 300,
        abbreviations,
        disabled,
        menuWidth,
        autoMenuWidthProps,
        menuHeightAdjustment = 0,
        sx,
        onChange
    } = props;

    const [searchValue, setSearchValue] = useState('');
    const [showSubMenu, setShowSubMenu] = useState(false);
    const [timer, setTimer] = useState<number | undefined>(undefined);
    const [selectedOptions, setSelectedOptions] = useState<Map<string, string>>(new Map());
    const [filteredOptions, setFilteredOptions] = useState<{ value: string; label: string }[]>(options);

    const popUpRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLDivElement | null>(null);

    const optionsLenght = options.length;
    const filteredOptionsLenght = filteredOptions.length;

    const menuHeight = Math.min(filteredOptionsLenght, maxItemsOnView) * menuItemSize + menuHeightAdjustment;
    const optionsMap: Map<string, string> = new Map(options.map(x => [x.value, x.label]));

    const skipNextUpdateSelections = useRef(false);

    useEffect(() => {
        if (!selections) return;
        if (skipNextUpdateSelections.current) {
            skipNextUpdateSelections.current = false;
            return;
        }

        const updatedSelections = new Map<string, string>();

        for (const value of selections) {
            const existingOption = options.find(option => option.value === value);
            if (existingOption) updatedSelections.set(existingOption.value, existingOption.label);
        }

        setSelectedOptions(updatedSelections);
    }, [selections]);

    useEffect(() => {
        if (selectAll) setSelectedOptions(new Map(optionsMap));
    }, [selectAll]);

    useEffect(() => {
        if (deselectAll) setSelectedOptions(new Map());
    }, [deselectAll]);

    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (
                popUpRef.current &&
                buttonRef.current &&
                !popUpRef.current.contains(event.target as Node) &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setSearchValue('');
                setShowSubMenu(false);
                setFilteredOptions(options);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [timer]);

    const calculateListWidth = useMemo(() => {
        if (menuWidth || !filteredOptions.length) return menuWidth ?? 100;

        const fontSize = autoMenuWidthProps?.fontSize ?? '16px';
        const fontFamily = autoMenuWidthProps?.fontFamily ?? 'proxima-nova';
        const font = `${fontSize} ${fontFamily}`;

        const maxLabelWidth = Math.max(...filteredOptions.map(option => measureTextWidth(option.label, font)));

        const checkboxWidth = autoMenuWidthProps?.checkboxWidth ?? 20;
        const adjustment = autoMenuWidthProps?.adjustment ?? 30;

        return maxLabelWidth + checkboxWidth + adjustment;
    }, [filteredOptions, menuWidth]);

    const handleClick = () => {
        setShowSubMenu(!showSubMenu);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const optionValue = event.target.name;
        const optionLabel = optionsMap.get(optionValue);
        if (!optionLabel) return;

        setSelectedOptions(prevSelectedOptions => {
            const newOptions = new Map(prevSelectedOptions);

            if (newOptions.has(optionValue)) {
                newOptions.delete(optionValue);
            } else {
                newOptions.set(optionValue, optionLabel);
            }

            skipNextUpdateSelections.current = true;
            onChange?.(value, Array.from(newOptions.keys()));
            return newOptions;
        });
    };

    const handleSelectAll = () => {
        if (!options) return;

        const allSelected = options.every(opt => selectedOptions.has(opt.value));
        const newOptions = allSelected ? new Map<string, string>() : new Map(optionsMap);

        setSelectedOptions(newOptions);
        skipNextUpdateSelections.current = true;
        onChange?.(value, Array.from(newOptions.keys()));
    };

    const onSearch = (text: string) => {
        setSearchValue(text);

        if (timer) clearTimeout(timer);

        const newTimer = setTimeout(() => {
            handleSearch(text);
        }, searchDelay);

        setTimer(newTimer);
    };

    const handleSearch = (text: string) => {
        if (!text) {
            setFilteredOptions(options);
            return;
        }

        const searchString = text.toLowerCase();
        setFilteredOptions(options.filter(option => option.label.toLowerCase().includes(searchString)));
    };

    const displaySelections = () => {
        if (!selectedOptions.size) return;

        const selections = Array.from(selectedOptions.values())
            .map(option => {
                const abbreviation = abbreviations?.get(option.toLowerCase());
                return abbreviation || option;
            })
            .join(', ');

        return `(${selections})`;
    };

    return (
        <Stack sx={sx?.container}>
            <Tooltip disableHoverListener={!tooltip} disableFocusListener={!tooltip} title={tooltip}>
                <div ref={buttonRef}>
                    <ToggleButton
                        disabled={disabled}
                        selected={selectedOptions.size > 0 || showSubMenu}
                        value={title}
                        sx={merge(styles.pill, sx?.button)}
                        onClick={handleClick}>
                        {title} {showSelections && displaySelections()} {showCount && selectedOptions.size > 0 && `(${selectedOptions.size})`}{' '}
                        <ArrowDropDown fontSize="small" />
                    </ToggleButton>
                </div>
            </Tooltip>

            {showSubMenu && (
                <Box sx={styles.popUpContainer}>
                    <Stack ref={popUpRef} sx={merge(styles.popUpMenu, sx?.menu)}>
                        {header && <Typography sx={merge(styles.text, sx?.text)}>{header}</Typography>}
                        {showSearchbox && (
                            <TextField
                                size="small"
                                variant="standard"
                                value={searchValue}
                                placeholder="Search"
                                sx={{ marginBottom: 1 }}
                                onChange={e => onSearch(e.target.value)}
                            />
                        )}
                        {showSelectAll && (
                            <>
                                <FormControlLabel
                                    componentsProps={{ typography: merge(styles.text, sx?.text) }}
                                    sx={styles.checkboxLabel}
                                    control={
                                        <Checkbox
                                            checked={selectedOptions.size >= optionsLenght}
                                            size="small"
                                            sx={styles.checkbox}
                                            onChange={handleSelectAll}
                                        />
                                    }
                                    label="Select All"
                                />
                                <Divider style={styles.divider} />
                            </>
                        )}
                        {!filteredOptionsLenght && <Typography sx={merge(styles.text, sx?.text)}>Not found</Typography>}
                        <FixedSizeList
                            height={menuHeight}
                            itemCount={filteredOptionsLenght}
                            itemSize={menuItemSize}
                            width={calculateListWidth}
                            style={{ overflowX: 'hidden' }}>
                            {({ index, style }) => {
                                const option = filteredOptions[index];
                                return (
                                    <div style={style} key={option.value}>
                                        <FormControlLabel
                                            key={option.value}
                                            componentsProps={{ typography: merge(styles.text, sx?.text) }}
                                            sx={styles.checkboxLabel}
                                            control={
                                                <Checkbox
                                                    checked={selectedOptions.has(option.value)}
                                                    size="small"
                                                    name={option.value}
                                                    sx={merge(styles.checkbox, sx?.checkbox)}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label={option.label}
                                        />
                                    </div>
                                );
                            }}
                        </FixedSizeList>
                    </Stack>
                </Box>
            )}
        </Stack>
    );
};
